import { array } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { PopularLazyLoadSelector } from 'webapp/utils/common';
import type { DuplicateCleanupConfig } from 'webapp/utils/html';
import { cleanupDuplicateNodes } from 'webapp/utils/html';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import type { Args } from 'webapp/components/boards/index/base';
import BoardsIndexBaseComponent from 'webapp/components/boards/index/base';
import BoardsIndexTableRowComponent from 'webapp/components/boards/index/table-row';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonAutoCreateOrCompleteBoardButtonComponent from 'webapp/components/common/auto-create-or-complete-board-button';
import CommonBatchButtonComponent from 'webapp/components/common/batch-button';
import CommonBatchTableHeaderComponent from 'webapp/components/common/batch-table-header';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import CommonRecentlyDeletedInfoComponent from 'webapp/components/common/recently-deleted-info';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import SkeletonScreenComponent from 'webapp/components/skeleton-screen';
import TableAddNewItemComponent from 'webapp/components/table/add-new-item';
import TemplatesTransferedNotificationAccountTableRowComponent from 'webapp/components/templates/transfered-notification/account/table-row';
import TemplatesTransferedNotificationPublicBoardTableRowComponent from 'webapp/components/templates/transfered-notification/public/board-table-row';

import type Board from 'webapp/models-custom/board';
import FilterSortBoardInList from 'webapp/models-custom/filter-sort/board-in-list';

export default class BoardsIndexTableComponent extends BoardsIndexBaseComponent<Args> {
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;

	override filterSort: FilterSortBoardInList;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = new FilterSortBoardInList({
			selectors: PopularLazyLoadSelector.table,
			getAllItems: () => {
				return this.args.model;
			},
			onOpenView: (viewId: string) => {
				this.args.onOpenView(viewId);
			},
			afterFilter: () => {
				// for visible-check
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}

				this.args.updateIsRecentlyDeletedFilterActive(filterSort.isRecentlyDeletedSelected);
			},
		});

		this.filterSort = filterSort;
	}

	get showPerformanceSummary() {
		return this.mmDataPerformanceSummariesService.showInUI;
	}

	get cellsInRowCount() {
		return this.showPerformanceSummary ? 9 : 8;
	}

	get lastUpdateColumnText() {
		return this.isRecentlyDeletedActiveWithResults ? 'Deleted' : 'Last update';
	}

	get duplicateCleanupConfig(): DuplicateCleanupConfig {
		return {
			sectionSelectors: ['.favorites-section', '.non-favorites-section'],
			itemSelector: 'tr.table-row[data-id]',
			shouldKeepInSection: (itemId: string, sectionSelector: string): boolean => {
				const board = this.args.model.find((b: Board) => b.id === itemId);
				if (!board) {
					return false;
				}
				return (
					(board.favorite && sectionSelector === '.favorites-section') ||
					(!board.favorite && sectionSelector === '.non-favorites-section')
				);
			},
		};
	}

	@action
	cleanupDuplicateNodes(): void {
		cleanupDuplicateNodes(this.duplicateCleanupConfig);
	}

	@action
	setupView(node: HTMLElement, sortingFavorites: boolean): void {
		this.visibleCheckContainerNode = node;

		const ctEl = $(node).find('tbody'),
			itemSelectorString = this.filterSort.itemDomSelector,
			placeholderElementFn = function (currentItem: JQuery) {
				const el = $('<tr />');
				el.addClass('drop-placeholder').append(
					`<td colspan="${currentItem.find('>td:not(.toggle)').length}"><div><ui-icon name="DragAndDropArea" size="xl" class="bounce"></ui-icon><span>Drop here</span></div></td>`,
				);
				return el;
			};
		this.initSortable(ctEl, itemSelectorString, placeholderElementFn, sortingFavorites);
	}

	@action
	setupFavoritesView(node: HTMLElement): void {
		this.setupView(node, true);
	}

	@action
	setupNonFavoritesView(node: HTMLElement): void {
		this.setupView(node, false);
	}

	@action
	teardownView(): void {
		this.destroySortable();
	}
	<template>
		<div class="bottom-padding sortable-table-container" {{willDestroy this.teardownView}}>
			<FilterLineProxyComponent @model={{this.filterSort}} />

			<FilterNoResultsComponent @model={{this.filterSort}} @entityName="dashboards" />

			{{#if this.isRecentlyDeletedActiveWithResults}}
				<CommonRecentlyDeletedInfoComponent
					@onDeleteAllPermanently={{this.deleteAllPermanently}}
				/>
			{{/if}}

			{{#if this.isRecentlyDeletedLoading}}
				<SkeletonScreenComponent @type="table" />
			{{/if}}

			{{! Notifications boards section }}
			<table
				class={{joinClasses
					(array
						"boards-table"
						"flexible-table"
						"bigtable"
						"has-checkbox"
						"has-avatar-big"
						(if this.showPerformanceSummary "has-performance-summary" "")
						"notifications-section"
					)
				}}
			>
				<colgroup>
					<col class="cc" />
					<col class="h0" />
					<col width="20%" />
					{{#if this.showPerformanceSummary}}
						<col class="data-performance-summary-cell" />
					{{/if}}
					<col width="10%" />
					<col />
					<col />
					<col width="10%" />
					<col class="button-ct" />
				</colgroup>

				<thead class={{unless this.isNotificationsSectionVisible "hidden"}}>
					<tr>
						{{#if this.isNotificationsSectionVisible}}
							<CommonBatchTableHeaderComponent @batchSelect={{this.batchSelect}} />
						{{else}}
							<th class="cc"></th>
						{{/if}}
						<th class="h0">Name</th>
						<th></th>
						{{#if this.showPerformanceSummary}}
							<th class="data-performance-summary-cell prio-low"></th>
						{{/if}}
						<th class="prio-medium">Data sources</th>
						<th class="prio-low">Shared with</th>
						<th class="status-cell">{{this.lastUpdateColumnText}}</th>
						<th class="prio-low">Tags</th>
						<th class="button-ct"></th>
					</tr>
				</thead>

				{{! don't add dynamic classes on tbody bcos of sortable }}
				<tbody>
					{{#unless this.isRecentlyDeletedActive}}
						{{#each @transferedBoardAccountTemplateNotifications as |notification|}}
							<TemplatesTransferedNotificationAccountTableRowComponent
								@model={{notification}}
								@cellsInRowCount={{this.cellsInRowCount}}
								@mediumPriorityCellsInRowCount={{1}}
								@lowPriorityCellsInRowCount={{3}}
								@extraClasses={{array "notification--account"}}
							/>
						{{/each}}

						{{#each @transferedBoardPublicTemplateNotifications as |notification|}}
							<TemplatesTransferedNotificationPublicBoardTableRowComponent
								@model={{notification}}
							/>
						{{/each}}
					{{/unless}}
				</tbody>
			</table>

			{{! Favorite boards section }}
			<CommonListLazyLoadComponent
				@filterSort={{this.filterSort}}
				@filterExtraFn={{this.favoriteBoardsFilterFn}}
				@initialRowCount={{10}}
				@loadMoreRowCount={{5}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{700}}
				@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
				as |visibleItems exporter|
			>
				<table
					class={{joinClasses
						(array
							"boards-table"
							"flexible-table"
							"bigtable"
							"has-checkbox"
							"has-avatar-big"
							(if this.showPerformanceSummary "has-performance-summary" "")
							"favorites-section"
							(if this.noResults "hide" "")
						)
					}}
					{{didUpdate this.triggerContentChange visibleItems}}
					{{visibleCheckContainer}}
					{{didInsert this.setupFavoritesView}}
					{{willDestroy this.teardownView}}
				>
					<colgroup>
						<col class="cc" />
						<col class="h0" />
						<col width="20%" />
						{{#if this.showPerformanceSummary}}
							<col class="data-performance-summary-cell" />
						{{/if}}
						<col width="10%" />
						<col />
						<col />
						<col width="10%" />
						<col class="button-ct" />
					</colgroup>

					<thead class={{unless this.isFavoriteSectionVisible "hidden"}}>
						<tr>
							{{#if this.isFavoriteSectionVisible}}
								<CommonBatchTableHeaderComponent
									@batchSelect={{this.batchSelect}}
								/>
							{{else}}
								<th class="cc"></th>
							{{/if}}
							<th class="h0">Name</th>
							<th></th>
							{{#if this.showPerformanceSummary}}
								<th class="data-performance-summary-cell prio-low"></th>
							{{/if}}
							<th class="prio-medium">Data sources</th>
							<th class="prio-low">Shared with</th>
							<th class="status-cell">{{this.lastUpdateColumnText}}</th>
							<th class="prio-low">Tags</th>
							<th class="button-ct"></th>
						</tr>
					</thead>

					{{! don't add dynamic classes on tbody bcos of sortable }}
					<tbody>
						{{#each visibleItems as |item|}}
							<BoardsIndexTableRowComponent
								@model={{item}}
								@batchSelect={{this.batchSelect}}
								@hideSort={{exporter.hideSort}}
								@onCopy={{@onCopy}}
								@onOpenPreview={{this.openPreview}}
							/>
						{{/each}}
					</tbody>
				</table>

				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>

			{{! Non-favorite boards section }}
			<CommonListLazyLoadComponent
				@filterSort={{this.filterSort}}
				@filterExtraFn={{this.nonFavoriteBoardsFilterFn}}
				@initialRowCount={{10}}
				@loadMoreRowCount={{5}}
				@animationProperty="id"
				@autoLoaded={{true}}
				@bottomDistance={{700}}
				@checkHTMLNodeValidity={{this.cleanupDuplicateNodes}}
				as |visibleItems exporter|
			>
				<table
					class={{joinClasses
						(array
							"boards-table"
							"flexible-table"
							"bigtable"
							"has-checkbox"
							"has-avatar-big"
							(if this.showPerformanceSummary "has-performance-summary" "")
							"non-favorites-section"
							(if this.noResults "hide" "")
						)
					}}
					{{didUpdate this.triggerContentChange visibleItems}}
					{{visibleCheckContainer}}
					{{didInsert this.setupNonFavoritesView}}
					{{willDestroy this.teardownView}}
				>
					<colgroup>
						<col class="cc" />
						<col class="h0" />
						<col width="20%" />
						{{#if this.showPerformanceSummary}}
							<col class="data-performance-summary-cell" />
						{{/if}}
						<col width="10%" />
						<col />
						<col />
						<col width="10%" />
						<col class="button-ct" />
					</colgroup>

					<thead class={{unless this.isNonFavoriteSectionVisible "hidden"}}>
						<tr>
							{{#if this.isNonFavoriteSectionVisible}}
								<CommonBatchTableHeaderComponent
									@batchSelect={{this.batchSelect}}
								/>
							{{else}}
								<th class="cc"></th>
							{{/if}}
							<th class="h0">Name</th>
							<th></th>
							{{#if this.showPerformanceSummary}}
								<th class="data-performance-summary-cell prio-low"></th>
							{{/if}}
							<th class="prio-medium">Data sources</th>
							<th class="prio-low">Shared with</th>
							<th class="status-cell">{{this.lastUpdateColumnText}}</th>
							<th class="prio-low">Tags</th>
							<th class="button-ct"></th>
						</tr>
					</thead>

					{{! don't add dynamic classes on tbody bcos of sortable }}
					<tbody>
						{{#each visibleItems as |item|}}
							<BoardsIndexTableRowComponent
								@model={{item}}
								@batchSelect={{this.batchSelect}}
								@hideSort={{exporter.hideSort}}
								@onCopy={{@onCopy}}
								@onOpenPreview={{this.openPreview}}
								@onPermanentDelete={{this.handleSingleItemPermanentDelete}}
								@onRestore={{this.handleSingleItemRestore}}
							/>
						{{/each}}
					</tbody>
				</table>

				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</CommonListLazyLoadComponent>

			{{#if this.addBtnShown}}
				<TableAddNewItemComponent
					@title={{this.addButtonText}}
					@addNewItem={{@onBoardCreate}}
					@model={{this.filterSort}}
				/>
			{{/if}}
		</div>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent
					@text={{this.addButtonText}}
					@callTrigger={{@onBoardCreateChoice}}
					@options={{this.addButtonOptions}}
					@onOptionClick={{this.doAddButtonAction}}
				/>
			{{/in-element}}
		{{/if}}

		{{#if this.batchButtonContainerNode}}
			{{#in-element this.batchButtonContainerNode}}
				<CommonBatchButtonComponent @model={{this.batchSelect}} />
			{{/in-element}}
		{{/if}}

		{{#if this.generateWithAIButtonContainerNode}}
			{{#in-element this.generateWithAIButtonContainerNode}}
				<CommonAutoCreateOrCompleteBoardButtonComponent
					@entryPoint={{this.createEntryPoint}}
				/>
			{{/in-element}}
		{{/if}}
	</template>
}
